import { useQuery } from "@apollo/client";
import { Button, Table } from "@fluidattacks/design";
import type { ColumnDef, Row } from "@tanstack/react-table";
import _ from "lodash";
import { StrictMode, useCallback, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { GET_TODO_VULN_REATTACKS } from "./queries";
import type { IFindingsFormatted, IVulnerabilityToReattack } from "./types";
import { formatVulnerabilities } from "./utils";

import { GET_USER_ORGANIZATIONS_GROUPS } from "../queries";
import type { IOrganizationGroups } from "@types";
import { useAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";
import { formatDateTime } from "utils/date";
import { Logger } from "utils/logger";

export const Reattacks: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { addAuditEvent } = useAudit();
  const columns: ColumnDef<IFindingsFormatted>[] = [
    {
      accessorKey: "groupName",
      header: "Group name",
      size: 300,
    },
    {
      accessorKey: "title",
      header: "Weakness",
      size: 350,
    },
    {
      accessorKey: "requested",
      header: "Requested vulns",
      size: 250,
    },
    {
      accessorKey: "oldestReattackRequestedDate",
      cell: (cell): string => formatDateTime(String(cell.getValue() ?? "")),
      header: "Reattack date",
    },
  ];

  const { data: userData } = useQuery(GET_USER_ORGANIZATIONS_GROUPS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning("An error occurred fetching user groups", error);
      });
    },
  });

  const { data, fetchMore } = useQuery(GET_TODO_VULN_REATTACKS, {
    onCompleted: (): void => {
      addAuditEvent("ToDo.Reattacks", "unknown");
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning(
          "An error occurred fetching user vulnerabilities from dashboard",
          error,
        );
      });
    },
    skip: _.isUndefined(userData),
    variables: { first: 1000 },
  });

  const organizations = userData === undefined ? [] : userData.me.organizations;
  const allVulnerabilities =
    _.isUndefined(data) || _.isEmpty(data)
      ? []
      : _.flatten(data.me.vulnerabilitiesReattacks.edges);
  const vulnerabilities = allVulnerabilities.map(
    (vuln): IVulnerabilityToReattack | null => {
      if (vuln === null) {
        return null;
      }

      return {
        finding: vuln.node.finding,
        groupName: vuln.node.groupName,
        id: vuln.node.id,
        lastRequestedReattackDate: vuln.node.lastRequestedReattackDate,
      };
    },
  );
  const dataset = formatVulnerabilities(
    organizations as IOrganizationGroups[],
    vulnerabilities.filter(
      (vuln): vuln is IVulnerabilityToReattack => vuln !== null,
    ),
  );
  const pageInfo = useMemo((): { endCursor: string; hasNextPage: boolean } => {
    return data === undefined
      ? { endCursor: "", hasNextPage: false }
      : data.me.vulnerabilitiesReattacks.pageInfo;
  }, [data]);

  useEffect((): void => {
    if (!_.isUndefined(pageInfo)) {
      if (pageInfo.hasNextPage) {
        void fetchMore({
          variables: { after: pageInfo.endCursor, first: 1000 },
        });
      }
    }
  }, [pageInfo, fetchMore]);

  const goToFinding = useCallback(
    (rowInfo: Row<IFindingsFormatted>): void => {
      const orgName = rowInfo.original.organizationName ?? "";

      navigate(
        `/orgs/${orgName}/groups/${rowInfo.original.groupName}/vulns/${rowInfo.original.id}/locations`,
      );
    },
    [navigate],
  );

  const formattedData = useMemo(
    (): IFindingsFormatted[] =>
      dataset.filter(({ groupName }): boolean =>
        groupName.toLowerCase().includes(search.toLowerCase()),
      ),
    [dataset, search],
  );

  const { table } = useTable({
    columns,
    data: formattedData,
    id: "reattacks-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: { onClick: goToFinding },
    },
    total: formattedData.length,
  });

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <div id={"actions-container"}>
              <Table.Export
                csvConfig={{
                  columns: [
                    "organizationName",
                    "groupName",
                    "id",
                    "title",
                    "requested",
                    "oldestReattackRequestedDate",
                    "url",
                  ],
                  export: true,
                }}
                data={formattedData}
              >
                <Button
                  icon={"file-export"}
                  id={"CSVExportBtn"}
                  tooltip={t("buttons.exportTip", { format: "CSV" })}
                  variant={"ghost"}
                >
                  {t("group.findings.exportCsv.text")}
                </Button>
              </Table.Export>
            </div>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
    </StrictMode>
  );
};
